import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";
import styles from "./CutOutImage.module.scss";

interface CutOutImageProps {
  imageSrc: string;
  imageAlt?: string;
  notchX: number;
  notchY: number;
  containerClassName?: string;
}

const CORNER_RADIUS = 20;

export const CutOutImage = ({
  imageSrc,
  imageAlt = "Image",
  notchX,
  notchY,
  containerClassName,
}: CutOutImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pathString, setPathString] = useState<string>("");

  const generatePath = useCallback(
    (w: number, h: number) => {
      const r = CORNER_RADIUS;
      const nx = w - notchX;

      let path = `M ${r} 0 `;
      path += `L ${nx - r} 0 `;
      path += `A ${r} ${r} 0 0 1 ${nx} ${r} `;
      path += `L ${nx} ${notchY - r} `;
      path += `A ${r} ${r} 0 0 0 ${nx + r} ${notchY} `;
      path += `L ${w - r} ${notchY} `;
      path += `A ${r} ${r} 0 0 1 ${w} ${notchY + r} `;
      path += `L ${w} ${h - r} `;
      path += `A ${r} ${r} 0 0 1 ${w - r} ${h} `;
      path += `L ${r} ${h} `;
      path += `A ${r} ${r} 0 0 1 0 ${h - r} `;
      path += `L 0 ${r} `;
      path += `A ${r} ${r} 0 0 1 ${r} 0 `;
      path += `Z`;

      return path;
    },
    [notchX, notchY],
  );

  useEffect(() => {
    let animationFrameId: number;

    const updatePath = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setPathString(generatePath(rect.width, rect.height));
        }
      }
    };

    updatePath();

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updatePath);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [generatePath]);

  const containerStyle: React.CSSProperties = {
    clipPath: pathString ? `path('${pathString}')` : "none",
    WebkitClipPath: pathString ? `path('${pathString}')` : "none",
  };

  return (
    <div
      ref={containerRef}
      className={`${styles["cutout-image-container"]} ${containerClassName ?? ""}`}
      style={containerStyle}
    >
      <Image
        src={imageSrc}
        alt={imageAlt}
        className={styles["cutout-image"]}
        fill
        sizes="(max-width: 767px) 50vw, (max-width: 1023px) 100vw, 768px"
      />
    </div>
  );
};
